import React, { useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Formik } from 'formik-v2';
import * as Yup from 'yup';
import { ActionButton } from '@ax2/common/components/ActionButton';
import formStyles from '../styles/components/form/Forms.module.css';
import base from '../styles/Base.module.css';
import DefaultPage from './DefaultPage';
import TextField from '../components/form/TextField';
import TextArea from '../components/form/TextArea';
import DropdownField from '../components/form/DropdownField';
import { DropdownOption } from '@ax2/common/components/Dropdown';
import SupportRequest, { SupportRequestType } from '../model/SupportRequest';
import { contactSupport } from '../rest/supportClient';
import _ from 'lodash';
import { useUserContext } from '../contexts/UserContext';

interface SupportFormValues {
  firstName: string;
  lastName: string;
  email: string;
  category: string;
  description: string;
}

const supportSchema = Yup.object<SupportFormValues>().shape({
  firstName: Yup.string()
    .min(1, 'Too short')
    .max(50, 'Too long')
    .required('First name is required'),
  lastName: Yup.string()
    .min(1, 'Too short')
    .max(50, 'Too long')
    .required('Last name is required'),
  email: Yup.string()
    .email('Must be a valid email')
    .required('Email is required'),
  category: Yup.string()
    .oneOf(_.values(SupportRequestType), 'Please select a valid category')
    .required('Support need category is required'),
  description: Yup.string()
    .min(10, 'Please provide more detailed description')
    .required('Description is required'),
});

const supportReasons: DropdownOption<SupportRequestType>[] = [
  {
    value: SupportRequestType.Technical,
    title: 'Technical issue - website issues, survey builder not working',
  },
  { value: SupportRequestType.Payment, title: 'Payment issue' },
  {
    value: SupportRequestType.MissingSomething,
    title: "Don't see what I need here",
  },
  {
    value: SupportRequestType.SurveyBuilder,
    title: 'Need help with survey builder',
  },
  { value: SupportRequestType.Invoice, title: 'Need to be invoiced' },
  {
    value: SupportRequestType.MoreInfo,
    title: 'More info on EVERSANA offerings',
  },
  { value: SupportRequestType.Output, title: 'I have my output. What now?' },
  { value: SupportRequestType.Other, title: 'Other' },
];

export interface SupportFromProps {
  onComplete?: () => void;
  firstName?: string;
  lastName?: string;
  email?: string;
  preselectedReason?: string;
}

export const SupportForm: React.FC<SupportFromProps> = ({
  onComplete,
  firstName,
  lastName,
  email,
  preselectedReason,
}) => {
  const [formError, setFormError] = useState<string | null>(null);
  const initialValues: SupportFormValues = {
    firstName: firstName || '',
    lastName: lastName || '',
    email: email || '',
    category: '',
    description: '',
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={supportSchema}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        contactSupport(
          new SupportRequest(
            SupportRequestType[
              values.category as keyof typeof SupportRequestType
            ],
            values.firstName,
            values.lastName,
            values.email,
            values.description
          )
        )
          .then(() => {
            setSubmitting(false);
            onComplete && onComplete();
          })
          .catch(e => {
            setSubmitting(false);
            setFormError(e.description || e.message || e.toString());
          });
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit} className={formStyles.form}>
          <div className={formStyles.formItems}>
            <div
              className={[
                formStyles.formRow,
                formStyles.formItemFullWidth,
              ].join(' ')}
            >
              <TextField
                name="firstName"
                label="First Name"
                labelNote="required"
                wrapperClassName={formStyles.formRowCell}
              />
              <TextField
                name="lastName"
                label="Last Name"
                labelNote="required"
                wrapperClassName={formStyles.formRowCell}
              />
            </div>
            <TextField
              name="email"
              label="Email"
              labelNote="required, work email preferred"
              wrapperClassName={formStyles.formItemFullWidth}
            />
            <DropdownField
              name="category"
              options={supportReasons}
              value={preselectedReason}
              label="Select Your Support Need"
              labelNote="required"
              wrapperClassName={formStyles.formItemFullWidth}
            />
            <TextArea
              name="description"
              label="Description of Your Support Need"
              labelNote="required"
              wrapperClassName={formStyles.formItemFullWidth}
              rows={4}
            />
            {formError ? (
              <div className={formStyles.formError}>{formError}</div>
            ) : null}
            <div className={formStyles.formButtons}>
              <ActionButton
                onClick={() => !isSubmitting && handleSubmit()}
                loading={isSubmitting}
                type="primary"
                text="Submit"
              />
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

const Support: React.FC<RouteComponentProps> = ({ location }) => {
  const [complete, setComplete] = useState(false);
  const user = useUserContext();
  const params = new URLSearchParams(location.search);
  const t = params.get('t');
  let reason: SupportRequestType | undefined = undefined;
  if (_.includes(_.keys(SupportRequestType), t)) {
    reason = t as SupportRequestType;
  }

  return (
    <DefaultPage header={{ title: 'Support' }}>
      <div className={[base.actionBox, base.actionBoxFullWidth].join(' ')}>
        {complete ? (
          <p className={base.sectionText}>
            Thank you for contacting our support!
            <br />
            <span
              className={base.link}
              onClick={() => {
                setComplete(false);
                return false;
              }}
            >
              Have another request?
            </span>
          </p>
        ) : (
          <SupportForm
            preselectedReason={reason}
            onComplete={() => setComplete(true)}
            firstName={(user && user.firstName) || ''}
            lastName={(user && user.lastName) || ''}
            email={(user && user.email) || ''}
          />
        )}
      </div>
    </DefaultPage>
  );
};

export default withRouter(Support);
